import {match, Pattern} from 'ts-pattern';

import {last} from 'ramda';

import {FinancingFieldResponseBody} from '@dms/api/core';

// `offset` is where the segment starts in the layer text, a stable React key.
export type TextWithSamplesSegment =
  | {type: 'text'; offset: number; value: string}
  | {type: 'field'; offset: number; label: string};

// Same token syntax the BE substitutes when it renders a financing promo photo. The capture group
// keeps the tokens in the `split` result, at the odd indexes.
const FIELD_TOKEN_SPLIT_PATTERN = /(\{\{[A-Z0-9_]+\}\})/;
const TOKEN_DELIMITER_LENGTH = 2;

const appendText = (segments: TextWithSamplesSegment[], offset: number, value: string) => {
  const previous = last(segments);
  if (previous?.type === 'text') {
    return [...segments.slice(0, -1), {...previous, value: previous.value + value}];
  }
  return value.length > 0 ? [...segments, {type: 'text' as const, offset, value}] : segments;
};

/**
 * Splits a text layer into what the canvas paints: a known `{{KEY}}` token becomes the field's
 * sample value (the exact string the server preview renders), or a label chip when the field has
 * no sample. An unknown token stays as typed.
 */
export const renderTextWithSamples = (
  text: string,
  fields: FinancingFieldResponseBody[]
): TextWithSamplesSegment[] => {
  let segments: TextWithSamplesSegment[] = [];
  let offset = 0;

  text.split(FIELD_TOKEN_SPLIT_PATTERN).forEach((part, index) => {
    const isToken = index % 2 === 1;
    const key = part.slice(TOKEN_DELIMITER_LENGTH, -TOKEN_DELIMITER_LENGTH);
    const field = isToken ? fields.find((candidate) => candidate.key === key) : undefined;
    segments = match(field)
      .with(Pattern.nullish, () => appendText(segments, offset, part))
      .with({sample: Pattern.string}, (known) => appendText(segments, offset, known.sample))
      .otherwise((known) => [...segments, {type: 'field' as const, offset, label: known.label}]);
    offset += part.length;
  });

  return segments;
};
